import type { GistItem } from "../../model/types";
import type { WorkspaceNavigationRow } from "../model/workspaceNavigationTypes";
import styles from "./WorkspaceGistNavigator.module.css";

export type WorkspaceGistTreeProps = {
  rows: WorkspaceNavigationRow[];
  getGistHref?: (gist: GistItem) => string;
  isDraftGist?: (gist: GistItem) => boolean;
  onSelectGist?: (gist: GistItem) => void;
};

export function WorkspaceGistTree({
  rows,
  getGistHref,
  isDraftGist,
  onSelectGist,
}: WorkspaceGistTreeProps) {
  if (!rows.length) {
    return (
      <p className={styles.emptyState}>
        No gists are available for this branch yet.
      </p>
    );
  }

  return (
    <ol className={styles.tree} aria-label="Workspace gist branch navigation">
      {rows.map((row) => {
        const title = row.gist.title || "Untitled gist";
        const href = getGistHref?.(row.gist);
        const isDraft = Boolean(isDraftGist?.(row.gist));
        const rowClassNames = [
          styles.row,
          row.isCurrent ? styles.rowCurrent : "",
          row.isAncestorOfCurrent ? styles.rowAncestor : "",
          row.isDescendantOfCurrent ? styles.rowDescendant : "",
        ]
          .filter(Boolean)
          .join(" ");

        const content = (
          <>
            <span className={styles.titleText}>{title}</span>
            <span className={styles.badges} aria-hidden="true">
              {row.isCurrent && (
                <span className={`${styles.badge} ${styles.currentBadge}`}>
                  current
                </span>
              )}
              {isDraft && (
                <span className={`${styles.badge} ${styles.draftBadge}`}>
                  draft
                </span>
              )}
              {row.hasChildren && <span className={styles.badge}>replies</span>}
            </span>
          </>
        );

        return (
          <li
            key={row.gist.id}
            className={rowClassNames}
            style={{ "--depth": row.depth } as React.CSSProperties}
            aria-current={row.isCurrent ? "page" : undefined}
          >
            <span className={styles.depthRail} aria-hidden="true" />
            {href ? (
              <a
                className={styles.link}
                href={href}
                onClick={(event) => {
                  if (!onSelectGist) return;
                  event.preventDefault();
                  onSelectGist(row.gist);
                }}
              >
                {content}
              </a>
            ) : (
              <button
                type="button"
                className={styles.button}
                onClick={() => onSelectGist?.(row.gist)}
              >
                {content}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
